import { ProjectDto } from '@app/shared/models/contracts/project-dto';
import {
  InstrumentType,
  OpmInputDataSource,
  InstrumentRank,
  EquityRefinancingMovementType,
  OwnershipSplitType,
  CouponType,
} from '@app/shared/models/contracts/enums/shared-enums';
import {
  cloneToMutableData,
  enumKeyByValue,
  generateUniqueName,
  instrumentTypeToName,
} from '@app/shared/helpers';
import { EquityItem } from '@app/modules/projects/inputs/capital-structure/CapitalStructureItemFormErf';
import { InstrumentDefinitionAndInitialValues } from '@app/core/store/capital-structure-slice-selectors';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const safeSetProperty = (obj: Record<string, any>, key: string, value: unknown): void => {
  const dangerousKeys = ['__proto__', 'constructor', 'prototype'];
  for (const dangerousKey of dangerousKeys) {
    if (key === dangerousKey) {
      throw new Error(`Rejected dangerous property name: ${key}`);
    }
  }

  if (Object.prototype.hasOwnProperty.call(obj, key) || !(key in obj)) {
    Object.assign(obj, { [key]: value });
  } else {
    Object.assign(obj, { [key]: value });
  }
};

export const getNewInstrument = (
  project: ProjectDto,
  eventInstruments: EquityItem[],
  instrumentType: InstrumentType,
  instrumentId: string,
  couponDate?: string
): InstrumentDefinitionAndInitialValues => {
  const startingRank =
    instrumentType === InstrumentType.OrdinaryEquity
      ? 0
      : Math.max(
          ...eventInstruments
            .filter((i) => i.rank !== InstrumentRank.OrdinaryEquity)
            .map((i) => i.rank),
          -1
        ) + 1;

  const instrumentTypeEnumKey = enumKeyByValue(InstrumentType, instrumentType);
  const ordinaryEquityEnumKey = enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity);

  const instrumentNewName = generateUniqueName(
    instrumentTypeToName(instrumentType),
    new Set(
      eventInstruments
        .filter((e) => e.type === instrumentTypeEnumKey)
        .map((e) => e.instrumentNarrative)
    )
  );

  return {
    instrumentNarrative: instrumentNewName,
    instrumentId: instrumentId,
    initialValues: {
      ownership: {},
      coupon:
        instrumentTypeEnumKey === ordinaryEquityEnumKey
          ? undefined
          : {
              value: 0,
              date: couponDate ?? project.investmentDate,
              type: enumKeyByValue(CouponType, CouponType.CompoundInterest),
              cashPayProportion: 0,
            },
    },
    payoutLogic: instrumentTypeEnumKey === ordinaryEquityEnumKey ? [] : null,
    type: instrumentTypeEnumKey,
    rank: startingRank,
    shouldBeValued: false,
    isSweetEquity: instrumentTypeEnumKey === ordinaryEquityEnumKey ? false : null,
    capType: null,
    capValue: null,
    dividendEligible: false,
  };
};

export const getProjectDraftWithInstrumentAddedToEvent = ({
  draft,
  eventId,
  newInstrument,
}: {
  draft: ProjectDto;
  eventId: string;
  newInstrument: InstrumentDefinitionAndInitialValues;
}): ProjectDto => {
  const capitalStructures = draft.capitalStructures ? { ...draft.capitalStructures } : {};
  const newInstrumentId = newInstrument.instrumentId;
  const capitalStructureId = Object.keys(capitalStructures)[0];
  const instrumentDefinitions = capitalStructures[capitalStructureId].instrumentDefinitions
    ? { ...capitalStructures[capitalStructureId]?.instrumentDefinitions }
    : {};
  safeSetProperty(instrumentDefinitions, newInstrumentId, {
    instrumentNarrative: newInstrument.instrumentNarrative,
    type: newInstrument.type,
    rank: newInstrument.rank,
    payoutLogic: [],
    shouldBeValued: newInstrument.shouldBeValued,
    isSweetEquity: newInstrument.isSweetEquity,
    capType: newInstrument.capType,
    capValue: newInstrument.capValue,
    dividendEligible: newInstrument.dividendEligible ?? false,
  });
  const events = capitalStructures[capitalStructureId].events
    ? { ...capitalStructures[capitalStructureId]?.events }
    : {};
  const movements = {
    ...events[eventId]?.movements,
  };
  safeSetProperty(movements, newInstrumentId, {
    movementType: EquityRefinancingMovementType.NewInstrument,
    ownerMovements: {},
    ownershipSplit: OwnershipSplitType.Manual,
    coupon: newInstrument.initialValues?.coupon ?? null,
  });
  safeSetProperty(events, eventId, {
    ...events[eventId],
    movements,
  });

  return {
    ...draft,
    opmInput: {
      ...draft.opmInput,
      perYearInputs: [
        ...(draft.opmInput.perYearInputs?.map((perYearInput) => {
          return {
            ...perYearInput,
            marketValueDloms: [
              ...perYearInput.marketValueDloms.filter(
                (dlomItem) => !(dlomItem.instrumentId === newInstrument.instrumentId)
              ),
              {
                instrumentId: newInstrument.instrumentId,
                instrumentDlom: null,
                instrumentDlomSource: OpmInputDataSource.FromPWERM,
                trancheDloms: [],
              },
            ],
          };
        }) ?? []),
      ],
    },
    pwermInput: {
      ...draft.pwermInput,
      cases: [
        ...draft.pwermInput.cases.map((caseItem) => ({
          ...caseItem,
          forecasts: caseItem.forecasts.map((forecast) => {
            return {
              ...forecast,
              dlom: [
                ...forecast.dlom.filter(
                  (dlomItem) =>
                    !(
                      dlomItem.instrumentId === newInstrument.instrumentId &&
                      dlomItem.trancheId === null
                    )
                ),
                {
                  instrumentId: newInstrument.instrumentId,
                  value: null,
                  trancheId: null,
                },
              ],
            };
          }),
        })),
      ],
    },
    capitalStructures: {
      ...draft.capitalStructures,
      [capitalStructureId]: {
        ...draft.capitalStructures[capitalStructureId],
        instrumentDefinitions: instrumentDefinitions,
        events: events,
      },
    },
  };
};

export const getProjectDraftWithAddedInstrument = ({
  draft,
  newInstrument,
}: {
  draft: ProjectDto;
  newInstrument: InstrumentDefinitionAndInitialValues;
}): ProjectDto => {
  const capitalStructures = draft.capitalStructures ? { ...draft.capitalStructures } : {};
  const newInstrumentId = newInstrument.instrumentId;
  const capitalStructureId = Object.keys(capitalStructures)[0];
  const instrumentDefinitions = capitalStructures[capitalStructureId].instrumentDefinitions
    ? { ...capitalStructures[capitalStructureId]?.instrumentDefinitions }
    : {};
  safeSetProperty(instrumentDefinitions, newInstrumentId, {
    instrumentNarrative: newInstrument.instrumentNarrative,
    type: newInstrument.type,
    rank: newInstrument.rank,
    payoutLogic: [],
    shouldBeValued: newInstrument.shouldBeValued,
    isSweetEquity: newInstrument.isSweetEquity,
    capType: newInstrument.capType,
    capValue: newInstrument.capValue,
    dividendEligible: newInstrument.dividendEligible ?? false,
  });
  const initialValues = capitalStructures[capitalStructureId].initialValues
    ? { ...capitalStructures[capitalStructureId]?.initialValues }
    : {};
  safeSetProperty(initialValues, newInstrumentId, {
    coupon:
      newInstrument.type !== enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity)
        ? {
            value: newInstrument.initialValues?.coupon?.value ?? 0,
            date: newInstrument.initialValues?.coupon?.date ?? draft.investmentDate,
            type: enumKeyByValue(CouponType, CouponType.CompoundInterest),
            cashPayProportion: 0,
          }
        : undefined,
    ownership: {},
  });

  return {
    ...draft,
    capitalStructures: {
      ...draft.capitalStructures,
      [capitalStructureId]: {
        ...draft.capitalStructures[capitalStructureId],
        instrumentDefinitions: instrumentDefinitions,
        initialValues: initialValues,
      },
    },
    opmInput: {
      ...draft.opmInput,
      perYearInputs: [
        ...(draft.opmInput.perYearInputs?.map((perYearInput) => {
          return {
            ...perYearInput,
            marketValueDloms: [
              ...perYearInput.marketValueDloms.filter(
                (dlomItem) => !(dlomItem.instrumentId === newInstrument.instrumentId)
              ),
              {
                instrumentId: newInstrument.instrumentId,
                instrumentDlom: null,
                instrumentDlomSource: OpmInputDataSource.FromPWERM,
                trancheDloms: [],
              },
            ],
          };
        }) ?? []),
      ],
    },
    pwermInput: {
      ...draft.pwermInput,
      cases: [
        ...draft.pwermInput.cases.map((caseItem) => ({
          ...caseItem,
          forecasts: caseItem.forecasts.map((forecast) => {
            return {
              ...forecast,
              dlom: [
                ...forecast.dlom.filter(
                  (dlomItem) =>
                    !(
                      dlomItem.instrumentId === newInstrument.instrumentId &&
                      dlomItem.trancheId === null
                    )
                ),
                {
                  instrumentId: newInstrument.instrumentId,
                  value: null,
                  trancheId: null,
                },
              ],
            };
          }),
        })),
      ],
    },
  };
};

export const getProjectDraftWithRemovedInstrument = ({
  draft,
  instrumentId,
}: {
  draft: ProjectDto;
  instrumentId: string;
}): ProjectDto => {
  const capitalStructures = draft.capitalStructures ? { ...draft.capitalStructures } : {};
  const capitalStructureId = Object.keys(capitalStructures)[0];
  const instrumentDefinitions = cloneToMutableData(
    capitalStructures[capitalStructureId].instrumentDefinitions
  );
  const events = cloneToMutableData(capitalStructures[capitalStructureId].events);
  const initialValues = cloneToMutableData(capitalStructures[capitalStructureId].initialValues);

  for (const eventId in events) {
    delete events[eventId].movements?.[instrumentId];
  }

  delete instrumentDefinitions[instrumentId];
  delete initialValues[instrumentId];

  const isInstrumentDefinitionsEmpty = Object.keys(instrumentDefinitions).length === 0;

  const forecastDividendSets = cloneToMutableData(
    draft.capitalStructures[capitalStructureId].forecastDividendSets
  );
  const historicDividendInstruments = cloneToMutableData(
    draft.capitalStructures[capitalStructureId].historicDividendInstruments
  );

  for (const dividendSet of Object.values(forecastDividendSets)) {
    if (Object.prototype.hasOwnProperty.call(dividendSet.instruments, instrumentId)) {
      delete dividendSet.instruments[instrumentId];
    }
  }

  if (Object.prototype.hasOwnProperty.call(historicDividendInstruments, instrumentId)) {
    delete historicDividendInstruments[instrumentId];
  }

  return {
    ...draft,
    capitalStructures: {
      ...draft.capitalStructures,
      [capitalStructureId]: {
        ...draft.capitalStructures[capitalStructureId],
        instrumentDefinitions: instrumentDefinitions,
        initialValues: initialValues,
        events: isInstrumentDefinitionsEmpty ? {} : events,
        eventSets: isInstrumentDefinitionsEmpty
          ? {}
          : draft.capitalStructures[capitalStructureId].eventSets,
        forecastDividendSets: forecastDividendSets,
        historicDividendInstruments: historicDividendInstruments,
      },
    },
    pwermInput: {
      ...draft.pwermInput,
      cases: [
        ...draft.pwermInput.cases.map((caseItem) => {
          return {
            ...caseItem,
            forecasts: caseItem.forecasts.map((forecastItem) => ({
              ...forecastItem,
              dlom: forecastItem.dlom.filter((dlomItem) => dlomItem.instrumentId !== instrumentId),
            })),
          };
        }),
      ],
    },
    opmInput: {
      ...draft.opmInput,
      netDebtPerEvent: isInstrumentDefinitionsEmpty ? {} : draft.opmInput.netDebtPerEvent,
    },
    overallConclusions: draft.overallConclusions.filter(
      (item) => item.instrumentId !== instrumentId
    ),
  };
};

export const getProjectDraftWithAddedDlom = ({
  draft,
  instrumentId,
  trancheId,
}: {
  draft: ProjectDto;
  instrumentId: string;
  trancheId?: string;
}): ProjectDto => {
  if (!trancheId) {
    return draft;
  }
  return {
    ...draft,
    pwermInput: {
      ...draft.pwermInput,
      cases: [
        ...draft.pwermInput.cases.map((_case) => ({
          ..._case,
          forecasts: _case.forecasts.map((forecast) => {
            return {
              ...forecast,
              dlom: [
                ...forecast.dlom.filter(
                  (dlomItem) =>
                    !(dlomItem.instrumentId === instrumentId && dlomItem.trancheId === null)
                ),
                {
                  instrumentId: instrumentId,
                  value: null,
                  trancheId: trancheId,
                },
              ],
            };
          }),
        })),
      ],
    },
  };
};

export const getProjectDraftWithRemovedDlom = ({
  draft,
  instrumentId,
  trancheId,
}: {
  draft: ProjectDto;
  instrumentId: string;
  trancheId?: string;
}): ProjectDto => {
  if (!trancheId) {
    return draft;
  }

  const updatedCases = draft.pwermInput.cases.map((caseItem) => {
    return {
      ...caseItem,
      forecasts: caseItem.forecasts.map((forecastItem) => {
        return {
          ...forecastItem,
          dlom: [
            ...forecastItem.dlom.filter(
              (dlomItem) =>
                !(dlomItem.instrumentId === instrumentId && dlomItem.trancheId === trancheId)
            ),
            ...(Object.values(draft.capitalStructures)[0].instrumentDefinitions[instrumentId]
              ?.payoutLogic?.length === 0
              ? [
                  {
                    instrumentId: instrumentId,
                    value: null,
                    trancheId: null,
                  },
                ]
              : []),
          ],
        };
      }),
    };
  });

  const updatedInstrumentDefinitions = {
    ...draft.capitalStructures,
    [Object.keys(draft.capitalStructures)[0]]: {
      ...draft.capitalStructures[Object.keys(draft.capitalStructures)[0]],
      instrumentDefinitions: {
        ...draft.capitalStructures[Object.keys(draft.capitalStructures)[0]].instrumentDefinitions,
        [instrumentId]: {
          ...draft.capitalStructures[Object.keys(draft.capitalStructures)[0]].instrumentDefinitions[
            instrumentId
          ],
          tranches: draft.capitalStructures[
            Object.keys(draft.capitalStructures)[0]
          ].instrumentDefinitions[instrumentId].payoutLogic?.filter(
            (tranche) => tranche.id !== trancheId
          ),
        },
      },
    },
  };

  return {
    ...draft,
    pwermInput: {
      ...draft.pwermInput,
      cases: updatedCases,
    },
    capitalStructures: updatedInstrumentDefinitions,
  };
};
